import type { DocumentMeta, Rect, TextLayer } from '../core/types';
import type { Engine } from './engine';
import { pixelOp, type EngineHistoryOp } from './historyOps';

function fullRect(doc: DocumentMeta): Rect {
  return { x: 0, y: 0, width: doc.width, height: doc.height };
}

export function textFontCss(layer: Pick<
  TextLayer,
  'fontStyle' | 'fontWeight' | 'fontSize' | 'fontFamily'
>): string {
  return `${layer.fontStyle} ${layer.fontWeight} ${layer.fontSize}px "${layer.fontFamily}", sans-serif`;
}

function linesOf(content: string): string[] {
  return content.length === 0 ? [''] : content.split('\n');
}

/** Axis-aligned box around the rendered glyphs, in document pixels. */
export function textLayerBounds(layer: TextLayer): Rect {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return { x: layer.x, y: layer.y - layer.fontSize, width: 1, height: layer.fontSize };
  }
  ctx.font = textFontCss(layer);
  const lines = linesOf(layer.content);
  const lineHeight = layer.fontSize * 1.2;
  let maxWidth = 1;
  for (const line of lines) {
    maxWidth = Math.max(maxWidth, ctx.measureText(line || ' ').width);
  }
  const height = Math.max(lineHeight, lines.length * lineHeight);
  let x = layer.x;
  if (layer.align === 'center') x -= maxWidth / 2;
  else if (layer.align === 'right') x -= maxWidth;
  // Baseline is near the first line; ascent ≈ 0.8em for typical fonts.
  const y = layer.y - layer.fontSize * 0.8;
  return {
    x: Math.floor(x) - 2,
    y: Math.floor(y) - 2,
    width: Math.ceil(maxWidth) + 4,
    height: Math.ceil(height) + 4,
  };
}

export function hitTestTextLayer(layer: TextLayer, point: { x: number; y: number }): boolean {
  const box = textLayerBounds(layer);
  return (
    point.x >= box.x &&
    point.x <= box.x + box.width &&
    point.y >= box.y &&
    point.y <= box.y + box.height
  );
}

/**
 * Rasterises editable text into its layer surface. Same history rules as shapes:
 * `record` is off during live typing so each keystroke is not a history entry.
 */
export function renderTextLayer(
  engine: Engine,
  doc: DocumentMeta,
  layer: TextLayer,
  record = true,
): EngineHistoryOp[] {
  const surface = engine.resourcesFor(doc).ensureLayer(layer.id).surface;
  const rect = fullRect(doc);
  const before = record ? surface.readPixels(rect) : null;

  const canvas = document.createElement('canvas');
  canvas.width = doc.width;
  canvas.height = doc.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    surface.target.clear(0, 0, 0, 0);
    return [];
  }

  ctx.clearRect(0, 0, doc.width, doc.height);
  ctx.font = textFontCss(layer);
  ctx.fillStyle = `rgba(${Math.round(layer.color.r * 255)}, ${Math.round(layer.color.g * 255)}, ${Math.round(layer.color.b * 255)}, ${layer.color.a})`;
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = layer.align;

  const lines = linesOf(layer.content);
  const lineHeight = layer.fontSize * 1.2;
  for (let i = 0; i < lines.length; i += 1) {
    ctx.fillText(lines[i]!, layer.x, layer.y + i * lineHeight);
  }

  const image = ctx.getImageData(0, 0, doc.width, doc.height);
  surface.writePixels(rect, new Uint8Array(image.data.buffer));

  if (!before) return [];
  return [pixelOp(layer.id, { rect, data: before }, { rect, data: surface.readPixels(rect) })];
}
